import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CTASection } from "../../../../components/cards/CTASection";
import { Icon } from "../../../../components/icon/icon";
import { SwitchInput } from "../../../../components/inputs/switch";

import { CancelEventModal } from "../../../../components/modal/app_modals/cancelEventModal";
import { DiscountModal } from "../../../../components/modal/app_modals/discountModal";
import { DuplicateEventModal } from "../../../../components/modal/app_modals/duplicateEventModal";
import { EventVisibilityModal } from "../../../../components/modal/app_modals/eventVisibilitymodal";
import { FeePayerModal } from "../../../../components/modal/app_modals/feePayerModal";
import { Pill } from "../../../../components/pill";
import { TableComponent } from "../../../../components/table";
import { MiniHeader } from "../../../../components/typography/miniHeader";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../../customeHooks/useModalControl";
import {
  useGetDiscountsQuery,
  useToggleAssignEventDiscountMutation,
} from "../../../../redux/features/event/event.api";
import {
  CreatedEvent,
  GetDiscoutQuery,
} from "../../../../redux/features/event/event.types";
import { PayoutCurrency } from "../../../../redux/features/payment/payment.types";
import { currencyShorts } from "../../../../utils/constants";
import { formatToCurrency } from "../../../../utils/helpers";
import { EventPermissions } from "../../../../utils/types";

export const EventSettings = ({
  event,
  refetchEvent = () => {},
  userEventPermissions,
  hasPaidTickets = false,
  eventCurrency,
}: {
  event: CreatedEvent;
  refetchEvent: () => void;
  userEventPermissions: EventPermissions[];
  hasPaidTickets?: boolean;
  eventCurrency: PayoutCurrency;
}) => {
  const [queryParams, setQueryParams] = useState<GetDiscoutQuery>({
    page: 1,
    limit: 20,
    search: undefined,
    sortOrder: "asc",
    status: undefined,
  });

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const navigate = useNavigate();

  const {
    data: disounts,
    isFetching: fetchingDiscounts,
    refetch: refetchDiscounts,
  } = useGetDiscountsQuery(
    { ...queryParams, currency: eventCurrency },
    {
      refetchOnMountOrArgChange: true,
      skip: !eventCurrency,
    },
  );

  const [toggleAssignEventDiscount, { isLoading: assigningEventDiscount }] =
    useToggleAssignEventDiscountMutation();

  return (
    <>
      <BgWrapper className="flex flex-col gap-3">
        <MiniHeader header="Discounts" />

        <TableComponent
          headers={[]}
          rows={
            disounts?.data?.data?.map((discount) => {
              return {
                id: discount._id,
                items: [
                  <div
                    className="flex flex-wrap items-center justify-between gap-5"
                    key={discount._id}
                  >
                    <div className="flex w-full flex-wrap items-center gap-5 md:w-fit md:flex-1">
                      <Icon
                        svg="circle-percent"
                        width={24}
                        height={24}
                        className=""
                      />

                      <div className="flex-1">
                        <MiniHeader
                          header={`${discount.name.toUpperCase()}`}
                          subheader={`${discount.type === "percentage" ? `${discount.value === 100 ? "FREE" : `${discount.value}% Discount`}` : `${formatToCurrency(discount.value, { currency: currencyShorts[discount?.currency ?? "NGN"] })} Discount`} ∙ ${discount.quantityType === "limited" ? `${discount.usageLimit}/${discount.quantity} Used` : "Unlimited"}`}
                          className="uppercase text-grey-900"
                        />
                      </div>
                    </div>

                    <Pill
                      text={
                        discount.status === "active" ? "ACTIVE" : "INACTIVE"
                      }
                      variation={
                        discount.status === "active" ? "green" : "grey"
                      }
                      className=""
                    />

                    <SwitchInput
                      checked={(
                        event?.discounts?.map((d) => d as unknown as string) ??
                        []
                      ).includes(discount._id ?? "")}
                      onChange={async (checked) => {
                        const resp = await toggleAssignEventDiscount({
                          eventId: event._id,
                          discountCode: discount.name ?? "",
                          discountCreatedBy: discount.createdBy._id,
                          status: checked ? "unassign" : "assign",
                        });

                        if (resp.data?.status === "success") {
                          refetchEvent();
                        }
                      }}
                      disabled={
                        discount.status !== "active" ||
                        assigningEventDiscount ||
                        !userEventPermissions.includes("assignDiscounts")
                      }
                      className="self-center"
                    />
                  </div>,
                ],
              };
            }) ?? []
          }
          isRowClickable={false}
          isTableEmpty={[undefined, 0].includes(disounts?.data?.data?.length)}
          emptyTitle="No Discount Added"
          emptySubTitle="Reduce ticket prices with discounts"
          emptyImage="/assets/images/percent-glass.png"
          ctaIcon="plus"
          isLoading={fetchingDiscounts}
          loadingRows={4}
          ctaText="Add Discount"
          onCTAClick={() => {
            handleModalOpen("discountModal");
          }}
          isPaginated
          currentPage={queryParams.page!}
          totalCount={disounts?.data?.totalCount ?? 0}
          itemsPerPage={queryParams.limit!}
          onPageChange={(page) => {
            setQueryParams({
              ...queryParams,
              page: page,
            });
          }}
        />
      </BgWrapper>

      <BgWrapper className="flex flex-col gap-3">
        <MiniHeader header="More Options" />

        <TableComponent
          headers={[]}
          rows={[
            {
              id: 1,
              items: [
                <CTASection
                  icon="globe"
                  title="Event Visibility"
                  text="Who can see your event page"
                  titleClassName=""
                  iconClassName=""
                  extra={
                    <p>
                      {event.visibility === "private" ? "Private" : "Public"}
                    </p>
                  }
                  onClick={() => {
                    handleModalOpen("eventVisibilityModal");
                  }}
                  disabled={!userEventPermissions.includes("editEventDetails")}
                />,
              ],
            },
            hasPaidTickets
              ? {
                  id: 2,
                  items: [
                    <CTASection
                      icon="ticket-percent"
                      title="Fees"
                      text="Who pays the fees on tickets"
                      titleClassName=""
                      iconClassName=""
                      extra={
                        <p>
                          {event.feePayer === "organiser"
                            ? "You’ll Pay The Fee"
                            : "Guests"}
                        </p>
                      }
                      onClick={() => {
                        handleModalOpen("feePayerModal");
                      }}
                      disabled={
                        !userEventPermissions.includes("editEventDetails")
                      }
                    />,
                  ],
                }
              : undefined,
            {
              id: 3,
              items: [
                <CTASection
                  icon="copy-plus"
                  title="Duplicate Event"
                  text="Create a copy of this event with all the event details"
                  titleClassName=""
                  iconClassName=""
                  extra={undefined}
                  onClick={() => {
                    handleModalOpen("duplicateEventModal");
                  }}
                  disabled={!userEventPermissions.includes("duplicateEvent")}
                />,
              ],
            },
            userEventPermissions.includes("cancelEvent")
              ? {
                  id: 4,
                  items: [
                    <CTASection
                      icon="calendar-off"
                      title="Cancel Event"
                      text="Cancel and remove this event, we’d notify the guests."
                      titleClassName="!text-danger"
                      iconClassName="!text-danger"
                      extra={undefined}
                      onClick={() => {
                        handleModalOpen("cancelEventModal");
                      }}
                      disabled={!userEventPermissions.includes("cancelEvent")}
                    />,
                  ],
                }
              : undefined,
          ]}
        />
      </BgWrapper>

      <DiscountModal
        open={modalOpen && modal === "discountModal"}
        handleClose={() => {
          handleModalClose();
        }}
        onSuccess={() => {
          handleModalClose();
          refetchDiscounts();
        }}
        mode="create"
      />

      <EventVisibilityModal
        open={modalOpen && modal === "eventVisibilityModal"}
        handleClose={() => {
          handleModalClose();
        }}
        event={event}
        onSuccess={() => {
          toast.success("Event Updated");
          handleModalClose();
          refetchEvent();
        }}
      />

      <FeePayerModal
        open={modalOpen && modal === "feePayerModal"}
        handleClose={() => {
          handleModalClose();
        }}
        event={event}
        onSuccess={() => {
          toast.success("Event Updated");
          handleModalClose();
          refetchEvent();
        }}
      />

      <DuplicateEventModal
        open={modalOpen && modal === "duplicateEventModal"}
        handleClose={() => {
          handleModalClose();
        }}
        event={event}
        onSuccess={() => {
          navigate("/events?EventStatus=draft");
        }}
      />

      <CancelEventModal
        open={modalOpen && modal === "cancelEventModal"}
        handleClose={() => {
          handleModalClose();
        }}
        event={event}
        onSuccess={() => {
          navigate("/events?EventStatus=completed");
        }}
      />
    </>
  );
};
